import {
  CircularProgress,
  createStyles,
  Grid,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { colors, DefaultButton, MessageBox, useFormHook } from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Constants
import profileTypes from 'src/constants/profileTypes';

// Models
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  RequestIndicatorTypes,
  Vehicle,
} from 'data-layer-cr/dist/models';
import {
  CAR_INFORMATION_STEP,
  LICENSE_PLATE,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';
import { TextValuesPayload } from '../../../../store/inspectionForm/slice/inspectionFormSlice';
import FormShape from './FormShape';

// Functions
import { formShapeToTextValues, makeInitialValues } from './dataMethods';
import makeValidationSchema from './makeValidationSchema';
import {
  makeImprovementsSection,
  makePreviousDamagesSection,
} from './viewMethods';

// Hooks
import { useCompleteCarIdentificationForm } from '../../hooks';

// Redux
import { setTextValues } from '../../../../store/inspectionForm/actions';
import { RootState } from '../../../../store/rootReducer';

// Components
import { AlreadyInsuredErrorDialogue } from '../../components';
import { QuotationSummaryModal } from '../QuotationSummary/components';
import {
  parseCustomerName,
  ViewQuotationSummary,
} from '../QuotationSummary/QuotationSummary';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridWrapper: {
      padding: theme.spacing(1),
    },
    gridWrapperMain: {
      padding: theme.spacing(1),
      paddingBottom: '0px',
    },
    gridTopBottomSpacing: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    cardPaddingTopBottom: {
      paddingTop: '12px',
      paddingBottom: '12px',
    },
    nextButton: {
      paddingTop: '24px',
      paddingBottom: '24px',
    },
    divTextField: {
      marginTop: '10px',
    },
    cardSeparation: {
      paddingTop: '18px',
    },
    infoCard: {
      position: 'relative',
    },
    carIcon: {
      position: 'absolute',
      right: theme.spacing(5),
    },
    carModelText: {
      color: colors.abbey,
      fontSize: 16,
    },
    textArea: {
      marginBottom: theme.spacing(2),
    },
    cardActions: {
      marginBottom: theme.spacing(-2),
    },
    dotted: {
      border: 0,
      height: 0,
      borderTop: `1px dashed ${colors.silver}`,
    },
  }),
);

export const InspectionData = ({
  quotation,
  coverageCatalog,
  coverageCatalogResumes,
  maxAllowedPayments,
  onSubmit,
  textValues,
  vehicle,
  setTextValuesAction,
  hasLicense,
  hasSerialMotor,
  profileType,
  inspectionCreationStatus,
}: Props): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const classes = useStyles();
  const [alreadyInsuredDialogueOpen, toggleAlreadyInsuredDialogueOpen] =
    useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const submitData = useCallback(
    (data: FormShape): void => {
      trackEvent({
        eventName: CAR_INFORMATION_STEP,
        eventProperties: {
          [LICENSE_PLATE]: data.licensePlate,
        },
      });
      trackCurrentStep(CAR_INFORMATION_STEP);
      setTextValuesAction(formShapeToTextValues(data));
      onSubmit();
    },
    [setTextValuesAction, onSubmit],
  );

  const initialValues = makeInitialValues({
    textValues,
    vehicle,
    profileType,
    hasLicense,
  });
  const {
    getInputProps,
    handleSubmit,
    updateMultipleInputs,
    providedAllRequired,
    errors,
  } = useFormHook<FormShape>(initialValues, submitData, makeValidationSchema());

  const onDialogueRequested = useCallback(
    () => toggleAlreadyInsuredDialogueOpen(true),
    [],
  );
  const {
    licensePlateInputCard,
    vinNumberInputCard,
    dataIsInvalid,
    policySource,
    serialMotorInputCard,
  } = useCompleteCarIdentificationForm({
    getInputProps,
    updateMultipleInputs,
    onDialogueRequested,
    hasLicense,
    hasSerialMotor,
  });

  const buttonIsDisabled = useMemo(
    () =>
      !providedAllRequired() || dataIsInvalid || Object.keys(errors).length > 0,
    [providedAllRequired, dataIsInvalid, errors],
  );

  const curLicense = getInputProps('licensePlate').value as string;
  const curVin = getInputProps('vinNumber').value as string;

  /**
   * This is a special case where the values need to be saved to the store as they are typed.
   * textValues are not passed to the dependency array, as they can trigger a render loop.
   */
  useEffect(() => {
    setTextValuesAction({
      ...textValues,
      licensePlate: curLicense,
      vinNumber: curVin,
      serialMotor: curVin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curLicense, curVin, setTextValuesAction]);

  const requestingCreationInspection =
    inspectionCreationStatus === 'requesting';

  const { fullName } = useMemo(() => parseCustomerName(quotation), [quotation]);

  const modelBrand = useMemo(
    () => `${quotation.summary.carBrand} ${quotation.summary.carModel}`,
    [quotation],
  );

  return (
    <>
      <Grid container spacing={2} className={classes.gridWrapperMain}>
        <QuotationSummaryModal
          data-testid="modal"
          open={showModal}
          quotation={quotation}
          coverageCatalog={coverageCatalog}
          coverageCatalogResumes={coverageCatalogResumes}
          maxAllowedPayments={maxAllowedPayments}
          onCloseRequested={(): void => setShowModal(false)}
        />
        <ViewQuotationSummary
          fullName={fullName}
          modelBrand={modelBrand}
          quotation={quotation}
          setShowModal={setShowModal}
        />
      </Grid>
      <form data-testid="inspectiondata-form" onSubmit={handleSubmit}>
        {/* Error dialogue to display when the car is already insured */}
        <AlreadyInsuredErrorDialogue
          data-testid="already-insured-dialogue"
          open={alreadyInsuredDialogueOpen}
          onCloseRequested={(): void => toggleAlreadyInsuredDialogueOpen(false)}
          source={policySource}
          licensePlate={curLicense}
          vinNumber={curVin}
          motorNumber={curVin}
        />
        {/* Main form */}
        <Grid container spacing={2} className={classes.gridWrapper}>
          <Grid item xs={12}>
            <MessageBox
              message={
                <div>
                  Asegúrate que los datos de Placa y VIN (número de chasis)
                  estén correctos para evitar inconvenientes con el tránsito.
                </div>
              }
              variant="info"
              hideCloseButton
            />
          </Grid>
          {/* License plate */}
          <Grid item xs={12}>
            {licensePlateInputCard}
          </Grid>

          {/* Vin Number */}
          <Grid item xs={12}>
            {vinNumberInputCard}
          </Grid>

          {/* Serial Motor */}
          {profileType === profileTypes.COLLECTIVE && (
            <Grid item xs={12}>
              {serialMotorInputCard}
            </Grid>
          )}

          {/* Previous Damages */}
          {makePreviousDamagesSection(getInputProps)}

          {/* Improvements */}
          {makeImprovementsSection(getInputProps)}

          <Grid item xs={12}>
            <MessageBox
              message={
                <div>
                  Recuerda que en ASSA cualquier mejora luego de la compra de tu
                  póliza, estará cubierta siempre que se compruebe que las
                  tenía.
                </div>
              }
              variant="info"
              hideCloseButton
            />
          </Grid>

          <Grid
            item
            xs={12}
            className={classes.cardSeparation}
            container
            alignItems="center"
            justifyContent="center"
          >
            <DefaultButton
              id="sales-web-inspection-button-next"
              disabled={buttonIsDisabled || requestingCreationInspection}
              data-testid="btn-submit"
              type="submit"
              fullWidth
            >
              {requestingCreationInspection ? (
                <CircularProgress data-testid="spinner-btn" color="inherit" />
              ) : (
                'Siguiente'
              )}
            </DefaultButton>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  coverageCatalog: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  maxAllowedPayments: number;
  textValues: TextValuesPayload;
  vehicle: Vehicle;
  hasLicense: boolean;
  hasSerialMotor: boolean;
  profileType: string | undefined;
  inspectionCreationStatus: RequestIndicatorTypes;
} => ({
  quotation: state.quotation.quotation,
  coverageCatalog: state.coverageCatalog.coverageCatalogs,
  coverageCatalogResumes: state.coverageCatalog.coverageCatalogResumes,
  maxAllowedPayments: state.emissionPolicy.paymentFields.paymentQuantity,
  textValues: state.inspectionForm.textValues,
  vehicle: state.quotation.quotation.units[0].unit,
  hasLicense: state.quotation.hasLicense,
  hasSerialMotor: state.quotation.hasSerialMotor,
  profileType: state.quotation.quotation.quotationProfile.type,
  inspectionCreationStatus: state.inspectionForm.inspectionCreationStatus,
});

const reduxActions = {
  setTextValuesAction: setTextValues,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(InspectionData);
